/* Presentation decisions for bulk runs and their reports.
 *
 * One distinction carries this screen, and D-04's own note names it: **undo reverts what the apply
 * *did*, not what it intended.** An item that was `skipped` — already in the requested state before the
 * run — is not touched by the undo, because reverting it would be a change nobody requested against a
 * state that predates the operation. A steward reading "412 selected, 38 already disabled" who then
 * undoes and finds 38 rows unchanged has been told that in advance or has found a bug.
 *
 * The second is that `partial` is its own state. A run that stopped at chunk 7 of 40 has changed real
 * rows, and rendering it as a flavour of `applied` or of `failed` is wrong in opposite directions —
 * "it worked" hides that 33 chunks did not run, "it failed" hides that 7 did.
 */

import type { BulkReport, BulkRun, RuleBinding } from '@/shared/api/generated/rules';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

/** The states a run can be in, and how each should read.
 *
 * A total map with `satisfies`, so an eighth state is a compile error rather than a row rendering with
 * whatever tone `undefined` produces. §9 records `verdictTone`'s `default:` giving an unknown verdict
 * the *permitted* tone in `core.ai.evals`, which stripped a failing row of its badge.
 */
export type RunState = BulkRun['state'];

export const STATE_TONE = {
  pending: 'neutral',
  previewing: 'neutral',
  applying: 'info',
  applied: 'ok',
  /* Not `ok` and not `error`. Real rows changed and real rows did not, so it is its own thing and the
   * badge has to say so or a reader picks one of the two wrong readings. */
  partial: 'warn',
  undoing: 'info',
  undone: 'neutral',
  failed: 'danger',
} as const satisfies Record<RunState, BadgeTone>;

/** Whether a run is still moving, so the screen can say so rather than showing a stale verdict. */
export const IN_FLIGHT: readonly RunState[] = [
  'pending',
  'previewing',
  'applying',
  'undoing',
];

export function isInFlight(run: BulkRun): boolean {
  return IN_FLIGHT.includes(run.state);
}

/** The per-status counts, in a fixed order, **including the zeroes**.
 *
 * The contract is explicit that the payload carries every status even at zero, and the reason is worth
 * restating where the rendering happens: a report showing only what happened lets "0 applied of 400"
 * and "400 applied" look alike. So this returns an entry per known status rather than iterating the
 * object's own keys — which would silently drop a zero the API bothered to send.
 */
export const STATUSES = ['applied', 'skipped', 'failed', 'not_attempted'] as const;

export type ItemStatus = (typeof STATUSES)[number];

export interface CountEntry {
  readonly status: ItemStatus;
  readonly count: number;
  /** True for a status that is not a problem, so a reader is not alarmed by a large `skipped`. */
  readonly benign: boolean;
}

export function countsOf(report: BulkReport): readonly CountEntry[] {
  const raw = report.counts as Record<string, unknown>;
  return STATUSES.map((status) => ({
    status,
    count: typeof raw[status] === 'number' ? raw[status] : 0,
    /* `skipped` is benign and is the one most likely to be a large number — it is what "already in
     * the requested state" looks like. Colouring it as a problem would train a steward to ignore the
     * column that actually matters. */
    benign: status === 'applied' || status === 'skipped',
  }));
}

/** How many items an undo would actually touch.
 *
 * **Derived from `applied` alone**, which is the whole point of this screen. The journal replays what
 * the apply *did*: a `skipped` item is not reverted, a `failed` one never changed, and a
 * `not_attempted` one belongs to a chunk that never ran. Presenting `total_items` as the undo's reach
 * would promise a steward that 412 rows move when 374 will.
 */
export function undoReach(report: BulkReport): number {
  const applied = (report.counts as Record<string, unknown>)['applied'];
  return typeof applied === 'number' ? applied : 0;
}

/** Whether every submitted item is accounted for, which is a property the report should hold.
 *
 * Rendered rather than asserted: if the counts do not sum to `total_items` the screen says so instead
 * of quietly showing four numbers that do not add up. A reader who spots that has found either a
 * partial write the journal did not record or a status this build does not know about — both worth
 * seeing, and both invisible if the screen only prints the four it recognises.
 */
export function accountsForEveryItem(report: BulkReport): boolean {
  const total = countsOf(report).reduce((sum, entry) => sum + entry.count, 0);
  return total === report.total_items;
}

/** The action, in words rather than in the kernel's dotted vocabulary. */
export function actionLabel(run: BulkRun): 'enable' | 'disable' {
  return run.action === 'rules.binding.enable' ? 'enable' : 'disable';
}

/* ------------------------------------------------------------------ the chunk ledger */

export type ChunkVerdict = 'committed' | 'restored' | 'running' | 'refused' | 'not_run';

export interface ChunkRow {
  /** "1–50", derived from the index and the run's chunk size — the journal stores an index. */
  readonly label: string;
  readonly verdict: ChunkVerdict;
  /** The exception type for a refused chunk, and never a driver message — the contract's rule. */
  readonly error: string | null;
}

/** Every chunk the run *should* have, each with what the journal says about it.

 * **Derived over the expected range, not mapped over the journal.** The journal holds only chunks
 * that ran, so a run that crashed at chunk 7 of 40 has seven journal rows — and a ledger drawn from
 * those alone would show seven green bars and read as complete. The 33 that never ran are the
 * finding, and they exist here as rows precisely because no row exists for them anywhere else.
 *
 * A journalled chunk that committed reads `committed` under the apply phase and `restored` under the
 * undo phase — same fact, opposite direction, and a reader of an undone run is looking at what the
 * *undo* did. One that did not commit is `refused`, carrying its exception type. An absent chunk is
 * `not_run` — untouched, which the design's own note insists is not a failure — except the first
 * absent one while the run is still moving, which is `running`: the journal is written chunk by
 * chunk in order, so the first gap is where the run is now.
 */
export function chunkRows(run: BulkRun, report: BulkReport): readonly ChunkRow[] {
  const expected = Math.max(
    1,
    Math.ceil(report.total_items / Math.max(1, run.chunk_size)),
  );
  const journalled = new Map<number, { committed: boolean; error: string | null }>();
  for (const raw of report.chunks) {
    const index = raw['chunk_index'];
    const committed = raw['committed'];
    if (typeof index !== 'number' || typeof committed !== 'boolean') continue;
    const error = raw['error'];
    journalled.set(index, {
      committed,
      error: typeof error === 'string' ? error : null,
    });
  }
  const moving = isInFlight(run);
  let firstGapMarked = false;
  const rows: ChunkRow[] = [];
  for (let index = 0; index < expected; index += 1) {
    const first = index * run.chunk_size + 1;
    const last = Math.min((index + 1) * run.chunk_size, report.total_items);
    const label = first === last ? String(first) : `${String(first)}–${String(last)}`;
    const entry = journalled.get(index);
    if (entry === undefined) {
      const verdict: ChunkVerdict = moving && !firstGapMarked ? 'running' : 'not_run';
      firstGapMarked = true;
      rows.push({ label, verdict, error: null });
    } else if (entry.committed) {
      rows.push({
        label,
        verdict: report.phase === 'undo' ? 'restored' : 'committed',
        error: null,
      });
    } else {
      rows.push({ label, verdict: 'refused', error: entry.error });
    }
  }
  return rows;
}

/* ------------------------------------------------------------------ the revert panel */

export type RevertStanding = 'available' | 'in_flight' | 'failed' | 'undone' | 'closed';

/** Why the revert is or is not on offer — five answers, because `undoable: false` covers four
 * different facts and each sends a reader somewhere different.

 * A run still moving will become revertable; a failed one changed nothing so there is nothing to
 * revert; an undone one has already been reverted; and a closed window means the change stands and
 * restoring it is now an ordinary edit made where the bindings are. Collapsing these into one
 * greyed-out control is the same defect the remediation screen shipped on `decision_refusal`: a
 * receipt rendered as a refusal.
 */
export function revertStanding(run: BulkRun): RevertStanding {
  if (run.undoable) return 'available';
  if (isInFlight(run)) return 'in_flight';
  if (run.state === 'failed') return 'failed';
  if (run.state === 'undone') return 'undone';
  return 'closed';
}

/** The header's second line: how many listed runs a revert is still on offer for. */
export function revertableCount(runs: readonly BulkRun[]): number {
  return runs.filter((run) => run.undoable).length;
}

/* ================================================================== starting a run */

/* The three numbers the API enforces, restated here so the screen can refuse before submitting
 * rather than after. Each is the *server's* bound: `MAX_BULK_ITEMS`, and `MIN_CHUNK_SIZE`/
 * `MAX_STORED_CHUNK_SIZE` — the last of which is narrower than the kernel's own maximum because the
 * substrate's CHECK is what actually refuses, and a caller-supplied 1,500 used to die there as a
 * 500 with the transaction doomed. */
export const SELECTION_CEILING = 5_000;
export const MIN_CHUNK = 1;
export const MAX_CHUNK = 1_000;
export const DEFAULT_CHUNK = 500;

/** Where the meter sits: a bar is not enough on its own to say a submission is refused. */
export const NEAR_CEILING = 0.8;

export type BulkAction = 'enable' | 'disable';

export function actionEvent(action: BulkAction): BulkRun['action'] {
  return action === 'enable' ? 'rules.binding.enable' : 'rules.binding.disable';
}

export interface CandidateFilters {
  /** A rule id, or `all`. */
  readonly rule: string;
  /** A severity, or `all`. */
  readonly severity: string;
  /** A glob over the binding's selectors: a trailing `*` is a prefix, anything else a substring. */
  readonly pattern: string;
}

/** Whether one binding's selectors match the typed pattern.

 * **Matched against the selector text, which is what the picker shows** — not against resolved
 * dataset names, which this screen never reads. A pattern that filtered on something invisible
 * would make the match count unexplainable from the rows on screen.
 *
 * A trailing `*` is a prefix and anything else is a substring, which is the affordance a person
 * types without being taught. Deliberately not a regex: a tenant-typed regex is a pattern nobody
 * on the team can reason about at 03:00, and `Selector.pattern`'s own contract says the same.
 */
export function matchesPattern(binding: RuleBinding, pattern: string): boolean {
  const wanted = pattern.trim().toLowerCase();
  if (wanted === '') return true;
  const prefix = wanted.endsWith('*') ? wanted.slice(0, -1) : null;
  return binding.selectors.some((selector) => {
    const text = (selector.pattern ?? selector.tag ?? selector.role ?? '').toLowerCase();
    if (text === '') return false;
    return prefix === null ? text.includes(wanted) : text.startsWith(prefix);
  });
}

export function matchesFilters(binding: RuleBinding, filters: CandidateFilters): boolean {
  if (filters.rule !== 'all' && binding.rule_id !== filters.rule) return false;
  if (filters.severity !== 'all' && binding.severity !== filters.severity) return false;
  return matchesPattern(binding, filters.pattern);
}

export interface SelectionFacts {
  readonly selected: number;
  /** Bindings not already in the requested state — what the run will actually change. */
  readonly willChange: number;
  /** Already in the requested state: skipped by the run, and left alone by any later revert. */
  readonly willSkip: number;
  readonly overCeiling: boolean;
  /** How far over, so the refusal can say it rather than only that there is one. */
  readonly excess: number;
  /** 0–1 against the ceiling, for the meter. */
  readonly fill: number;
}

/** What a selection would do, before anything is submitted.

 * **`willChange` and `willSkip` are the whole point of this panel.** A binding already in the
 * requested state is `skipped` by the run and untouched by a revert, so a confirmation promising
 * the selected total would over-promise: "412 selected" where 374 move. The run's own report makes
 * the same split afterwards; making it *before* is what lets somebody choose differently.
 */
export function selectionFacts(
  selected: readonly RuleBinding[],
  action: BulkAction,
): SelectionFacts {
  const wantEnabled = action === 'enable';
  const willChange = selected.filter((binding) => binding.enabled !== wantEnabled).length;
  return {
    selected: selected.length,
    willChange,
    willSkip: selected.length - willChange,
    overCeiling: selected.length > SELECTION_CEILING,
    excess: Math.max(0, selected.length - SELECTION_CEILING),
    fill: Math.min(1, selected.length / SELECTION_CEILING),
  };
}

export type MeterTone = 'normal' | 'near' | 'over';

export function meterTone(facts: SelectionFacts): MeterTone {
  if (facts.overCeiling) return 'over';
  return facts.fill > NEAR_CEILING ? 'near' : 'normal';
}

export function chunkIsValid(raw: string): boolean {
  /* Parsed strictly: `parseInt` accepts "500abc", and a chunk size the field showed as valid and
     the API then refused would be the interface withholding what it already knew. */
  return /^\d+$/.test(raw.trim()) && withinChunkBounds(Number(raw.trim()));
}

export function withinChunkBounds(value: number): boolean {
  return Number.isInteger(value) && value >= MIN_CHUNK && value <= MAX_CHUNK;
}

export type SubmitBlock = 'none' | 'empty' | 'over' | 'noop' | 'chunk';

/** Why a submission is not on offer — one answer, in the order a reader can act on.

 * Five values rather than a boolean for the reason the revert panel has five: a disabled button
 * with no sentence is a screen somebody concludes is broken. The order is deliberate — `over` is
 * checked before `noop`, because an over-ceiling selection has to be narrowed whatever else is
 * true of it, and telling somebody "nothing would change" while they are 400 bindings over the
 * limit sends them to the wrong problem.
 */
export function submitBlock(facts: SelectionFacts, chunk: string): SubmitBlock {
  if (facts.selected === 0) return 'empty';
  if (facts.overCeiling) return 'over';
  if (!chunkIsValid(chunk)) return 'chunk';
  if (facts.willChange === 0) return 'noop';
  return 'none';
}

/** How many chunks a run would be journalled in, or `null` when the size is unusable. */
export function chunkCount(facts: SelectionFacts, chunk: string): number | null {
  if (!chunkIsValid(chunk)) return null;
  /* Over the *selection*, not over `willChange`: every submitted id gets a verdict and travels in
     a chunk, including the ones that will be skipped. Dividing by what changes would under-report
     the chunk count on exactly the runs where most items are no-ops. */
  return Math.max(1, Math.ceil(facts.selected / Number(chunk.trim())));
}
